// Ready-made emails: "Your year" (dated, one or two a month) and "Library" (send any time).
// Both are photo walls. Tap a photo to preview it, then "Use this email" makes it a draft.
// Library is a tab here, not its own page, so every ready-made email lives in one place.

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api } from '../lib/api';
import { linkClick, navigate, useLocation } from '../lib/router';
import { Button, LoadError, Spinner, toast, toastError, useLoad } from '../lib/ui';
import { Icon } from '../lib/icons';
import { centralYmd, plural } from '../lib/format';
import { previewHtml } from '../lib/preview';
import { useSettings } from '../lib/store';
import { blockImages } from '../../shared/blocks';
import { libraryEmails, type PlannedEmail } from '../../shared/library';
import { PURPOSES, purposeLabel, purposeOf, type Purpose } from '../../shared/library/purpose';
import { addDays, daysBetween, planDate, upNext, welcomeEmails, yearEmails } from '../../shared/plan';
import type { EmailSummary } from '../../shared/types';

type Used = Map<string, EmailSummary>;
type Crop = 'w' | 't' | 's';

const usedMap = (emails: EmailSummary[]): Used => new Map(emails.filter((e) => e.library_key).map((e) => [e.library_key!, e]));

/** A photo for a card. Year photos come in wide (w), tall (t) and square (s) crops. */
function photoOf(p: PlannedEmail, crop: Crop): string | null {
  const imgs = p.blocks.flatMap((b) => blockImages(b));
  const year = imgs.find((i) => /^\/stock\/year\/.+-[wts]\.jpg$/.test(i.src));
  if (year) return year.src.replace(/-[wts]\.jpg$/, `-${crop}.jpg`);
  return imgs[0]?.src ?? null;
}

/** Card titles hide fill-in brackets like "[days]". The editor still shows them so Jen fills them in. */
export const cardTitle = (s: string): string => {
  const t = s.replace(/:?\s*\[[^\]]*\]\s*/g, ' ').replace(/\s+/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** Library emails that fit a time of year, by month (1-12). Taken from each email's own "send in..." line. */
const GOOD_IN: Record<string, number[]> = {
  'lib-seasons-holiday-recovery': [1],
  'lib-seasons-galentines': [1, 2],
  'lib-seasons-spring-shelf': [3, 4],
  'lib-seasons-fourth-of-july': [6],
  'lib-seasons-spot-check': [8],
  'lib-seasons-labor-day-legs': [9],
  'lib-seasons-first-frost': [10],
  'lib-seasons-gift-card-drawer': [1],
  'lib-moments-thanksgiving-host': [10],
  'lib-moments-own-wedding': [1],
  'lib-moments-mother-of-the-couple': [3, 4],
  'lib-moments-wedding-guest': [4, 5],
  'lib-moments-class-reunion': [5, 6],
  'lib-moments-beach-trip': [2],
  'lib-moments-family-photos': [9],
  'lib-moments-headshot': [1, 8],
  'lib-moments-graduation': [4],
  'lib-moments-girls-trip': [9],
  'lib-moments-anniversary-trip': [2, 3, 4],
  'lib-moments-back-to-school-you': [8, 9],
  'lib-moments-lake-season': [3],
  'lib-moments-office-party': [11],
  'lib-news-holiday-hours': [12],
  'lib-news-small-spa-thanks': [11],
};
const monthOf = (today: string) => Number(today.slice(5, 7));
const goodNow = (p: PlannedEmail, today: string) => !!GOOD_IN[p.key]?.includes(monthOf(today));

/** Library emails that fit this month and she has not used yet. Seasonal ones first. */
function forThisMonth(used: Used, today: string): PlannedEmail[] {
  const fit = libraryEmails().filter((p) => goodNow(p, today) && !used.has(p.key));
  return [...fit.filter((p) => purposeOf(p) === 'seasonal'), ...fit.filter((p) => purposeOf(p) !== 'seasonal')];
}

const at = (day: string) => new Date(`${day}T12:00:00Z`);
const niceDate = (day: string) => at(day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
const shortDate = (day: string) => at(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const monthName = (day: string) => at(day).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
const monthShort = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short' });

/** Phones and tablets tap; computers click. */
export const tapWord = () => (typeof matchMedia === 'function' && matchMedia('(hover: none), (max-width: 720px)').matches ? 'Tap' : 'Click');

/** "in 3 weeks", "tomorrow", "5 days ago" */
function fromToday(day: string, today: string): string {
  const d = daysBetween(today, day);
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d === -1) return 'yesterday';
  if (d < 0) return `${plural(-d, 'day')} ago`;
  if (d < 14) return `in ${plural(d, 'day')}`;
  if (d < 60) return `in ${Math.round(d / 7)} weeks`;
  return `in ${Math.round(d / 30)} months`;
}

/** Still being worked on: the button reopens the editor instead of the report. */
const isOpen = (e: EmailSummary) => e.status === 'draft' || e.status === 'scheduled';

/** Makes (or reopens) the draft for a ready-made email and goes to the editor. */
async function openPlanned(p: PlannedEmail): Promise<void> {
  const r = await api<{ id: number; existing?: boolean }>('/api/emails', { body: { library: p.key } });
  if (!r.existing) toast('Your draft is ready. Change anything you like.');
  navigate(`/emails/${r.id}/edit`);
}

/** Cards slide up a little as they scroll into view. Cards are never hidden: only their position moves.
 *  Cards already on screen when the wall opens do not move. Skipped when the device asks for less motion. */
function useReveal(key: unknown) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || !('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    root.classList.add('reveal');
    const fresh = Array.from(root.querySelectorAll<HTMLElement>('.wall-card:not([data-in])'));
    const fold = innerHeight;
    for (const c of fresh) if (c.getBoundingClientRect().top < fold) c.dataset.in = '0'; // already visible: no motion
    let n = 0;
    let last = 0;
    const io = new IntersectionObserver(
      (entries) => {
        const now = performance.now();
        if (now - last > 150) n = 0; // a new batch starts its stagger from zero
        last = now;
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const el = e.target as HTMLElement;
          el.style.setProperty('--d', `${Math.min(n++, 7) * 50}ms`);
          el.dataset.in = '1';
          io.unobserve(el);
        }
      },
      { rootMargin: '0px 0px -30px 0px' },
    );
    fresh.filter((c) => !c.dataset.in).forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [key]);
  return ref;
}

type Tab = 'year' | 'library';

export function Year() {
  const { query } = useLocation();
  const tab: Tab = query.get('tab') === 'library' ? 'library' : 'year';
  const list = useLoad(() => api<{ emails: EmailSummary[] }>('/api/emails').then((r) => r.emails), []);
  const used = useMemo(() => usedMap(list.data || []), [list.data]);
  const [open, setOpen] = useState<PlannedEmail | null>(null);
  const setTab = (t: Tab) => navigate(t === 'library' ? '/year?tab=library' : '/year', { replace: true, keepScroll: true });

  return (
    <div class="wrap">
      <a href="/" class="back-link" onClick={(e) => linkClick(e, '/')}>
        <Icon name="back" size={16} /> Emails
      </a>
      <div class="lib-head">
        <div>
          <h1 class="display">Written for you</h1>
          <p class="lead">
            {tapWord()} a photo to see it. Nothing changes until you {tapWord().toLowerCase()} Use this email.
          </p>
        </div>
        <div class="lib-tabs" role="tablist" aria-label="Ready-made emails">
          <button role="tab" aria-selected={tab === 'year'} class={tab === 'year' ? 'on' : ''} onClick={() => setTab('year')}>
            <Icon name="calendar" size={17} /> Your year
          </button>
          <button role="tab" aria-selected={tab === 'library'} class={tab === 'library' ? 'on' : ''} onClick={() => setTab('library')}>
            <Icon name="heart" size={17} /> Library
          </button>
        </div>
      </div>

      {list.error && <LoadError error={list.error} retry={list.reload} />}
      {!list.data && !list.error && <Spinner />}
      {list.data && (tab === 'year' ? <YearWall used={used} onOpen={setOpen} /> : <LibraryWall used={used} onOpen={setOpen} />)}

      {open && <PreviewSheet p={open} used={used.get(open.key)} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ---------- Your year ----------

function YearWall({ used, onOpen }: { used: Used; onOpen: (p: PlannedEmail) => void }) {
  const today = centralYmd();
  const plan = yearEmails();
  const dated = nextDated(used, today);
  const next = dated?.p ?? null;
  const wall = useReveal(used);
  const allDone = !next && plan.some((p) => used.has(p.key));
  // One group per month, so each month name sits over its own cards.
  const months: PlannedEmail[][] = [];
  for (const p of plan) {
    const last = months[months.length - 1];
    if (last && planDate(last[0])!.slice(0, 7) === planDate(p)!.slice(0, 7)) last.push(p);
    else months.push([p]);
  }

  return (
    <>
      {dated && <NextRow d={dated} today={today} onOpen={() => (dated.draftId ? navigate(`/emails/${dated.draftId}/edit`) : onOpen(dated.p))} />}
      {allDone && (
        <p class="year-done">
          <Icon name="sparkle" size={18} /> Your whole year is ready. Nice work.
        </p>
      )}

      <div class="wall wall-year" ref={wall}>
        {months.map((group, gi) => {
          const first = planDate(group[0])!;
          const showYear = !gi || first.slice(5, 7) === '01';
          return (
            <section class="wall-group" data-n={group.length} key={first.slice(0, 7)} style={{ gridColumn: `span ${group.length}` }}>
              <h2 class="wall-month">
                {monthName(first)}
                {showYear && <small> {first.slice(0, 4)}</small>}
              </h2>
              {group.map((p) => {
                const day = planDate(p)!;
                const u = used.get(p.key);
                const state: CardState = p.key === next?.key ? 'next' : u ? (u.status === 'draft' ? 'draft' : 'done') : daysBetween(today, day) < -10 ? 'past' : '';
                const pill = state === 'next' ? `Up next · ${shortDate(day)}` : state === 'draft' ? `Draft · ${shortDate(day)}` : shortDate(day);
                return <PhotoCard key={p.key} p={p} state={state} pill={pill} onOpen={() => onOpen(p)} />;
              })}
            </section>
          );
        })}
      </div>

      <WelcomeCard used={used} />
    </>
  );
}

/** One short row above the wall. The ringed card below shows the same email, so this stays small. */
function NextRow({ d, today, onOpen }: { d: Dated; today: string; onOpen: () => void }) {
  const p = d.p;
  const day = planDate(p)!;
  const photo = photoOf(p, 's');
  return (
    <button class="next-row" onClick={onOpen}>
      {photo && <img src={photo} alt="" />}
      <span class="next-row-text">
        <small>
          Up next · {niceDate(day)}, {fromToday(day, today)}
          {d.draftId && ' · your draft'}
        </small>
        <b>{cardTitle(p.subject)}</b>
      </span>
      <span class="next-row-cta">
        {d.draftId ? 'Open your draft' : 'Make it yours'} <Icon name="next" size={16} />
      </span>
    </button>
  );
}

// ---------- Library ----------

type Filter = Purpose | 'all';
const catLabel = (p: PlannedEmail) => purposeLabel(purposeOf(p));

function LibraryWall({ used, onOpen }: { used: Used; onOpen: (p: PlannedEmail) => void }) {
  const all = libraryEmails();
  const [cat, setCat] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);

  const shown = useMemo(() => {
    const inCat = cat === 'all' ? all : all.filter((p) => purposeOf(p) === cat);
    if (!words.length) return inCat;
    return inCat.filter((p) => {
      const hay = `${p.subject} ${p.name} ${p.preheader} ${p.why} ${catLabel(p)}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [cat, q]);

  const wall = useReveal(`${cat}|${q}|${used.size}`);
  const groups = cat === 'all' && !words.length ? PURPOSES.map((c) => ({ c, items: shown.filter((p) => purposeOf(p) === c.id) })).filter((g) => g.items.length) : null;
  const hint = cat !== 'all' && PURPOSES.find((c) => c.id === cat)?.hint;
  const today = centralYmd();
  const card = (p: PlannedEmail, tag?: string) => {
    const u = used.get(p.key);
    const state: CardState = u ? (u.status === 'draft' ? 'draft' : 'done') : goodNow(p, today) ? 'now' : '';
    const pill = !u ? (state === 'now' ? 'Good for now' : tag) : u.status === 'draft' ? 'Draft' : u.status === 'scheduled' ? 'Scheduled' : u.finished_at || u.started_at ? `Sent in ${monthShort((u.finished_at || u.started_at)!)}` : tag;
    return <PhotoCard key={p.key} p={p} state={state} pill={pill} cat={purposeOf(p)} onOpen={() => onOpen(p)} />;
  };

  return (
    <>
      <div class="lib-tools">
        <label class="lib-search">
          <Icon name="search" size={18} />
          <input type="search" value={q} placeholder="Search" aria-label="Search the library" onInput={(e) => setQ(e.currentTarget.value)} />
        </label>
        <div class="lib-chips" role="group" aria-label="Kind of email">
          <button class={`lib-chip ${cat === 'all' ? 'on' : ''}`} aria-pressed={cat === 'all'} onClick={() => setCat('all')}>
            All
          </button>
          {PURPOSES.map((c) => (
            <button key={c.id} data-cat={c.id} class={`lib-chip ${cat === c.id ? 'on' : ''}`} aria-pressed={cat === c.id} onClick={() => setCat(cat === c.id ? 'all' : c.id)}>
              <i />
              {c.label}
            </button>
          ))}
        </div>
      </div>
      {hint && <p class="lib-hint">{hint}</p>}

      <div ref={wall}>
        {!shown.length ? (
          <div class="lib-empty">
            <p>Nothing matches "{q.trim()}".</p>
            <Button kind="quiet" icon="x" onClick={() => setQ('')}>
              Clear search
            </Button>
          </div>
        ) : groups ? (
          groups.map((g) => (
            <section key={g.c.id} class="lib-group" data-cat={g.c.id}>
              <h2 class="lib-group-title">
                <i />
                {g.c.label}
              </h2>
              <div class="wall">{g.items.map((p) => card(p))}</div>
            </section>
          ))
        ) : (
          <div class="wall">{shown.map((p) => card(p, words.length && cat === 'all' ? catLabel(p) : undefined))}</div>
        )}
      </div>
    </>
  );
}

// ---------- shared pieces ----------

type CardState = 'next' | 'done' | 'draft' | 'past' | 'now' | '';

function PhotoCard({ p, state, pill, cat, onOpen }: { p: PlannedEmail; state: CardState; pill?: string; cat?: Purpose; onOpen: () => void }) {
  const photo = photoOf(p, 't');
  return (
    <button class={`wall-card ${state ? `is-${state}` : ''}`} data-cat={cat} onClick={onOpen}>
      <span class="wall-photo">
        {photo && <img src={photo} alt="" loading="lazy" />}
        {pill && <span class="wall-pill">{pill}</span>}
        {state === 'done' && (
          <span class="wall-check" title="Used">
            <Icon name="check" size={15} stroke={2.6} />
          </span>
        )}
      </span>
      <span class="wall-subject">
        {cardTitle(p.subject)}
        {state === 'done' && <span class="lib-sr"> (used)</span>}
      </span>
    </button>
  );
}

/** Bottom sheet on a phone, a centered card on a computer. Photo first, then the words, then one button. */
function PreviewSheet({ p, used, onClose }: { p: PlannedEmail; used?: EmailSummary; onClose: () => void }) {
  const s = useSettings();
  const [busy, setBusy] = useState(false);
  const [whole, setWhole] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const day = planDate(p);
  const today = centralYmd();
  const photo = photoOf(p, 'w');
  const html = useMemo(() => (s ? previewHtml(p, s.settings, false, day ? at(day) : undefined) : ''), [s, p]);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', key);
    document.body.classList.add('has-modal');
    box.current?.querySelector<HTMLElement>('.btn-primary')?.focus();
    return () => {
      document.removeEventListener('keydown', key);
      document.body.classList.remove('has-modal');
      prev?.focus?.();
    };
  }, []);

  const go = async () => {
    setBusy(true);
    try {
      if (used) navigate(isOpen(used) ? `/emails/${used.id}/edit` : `/emails/${used.id}`);
      else await openPlanned(p);
    } catch (e) {
      toastError(e);
      setBusy(false);
    }
  };
  const label = !used ? 'Use this email' : isOpen(used) ? 'Open your draft' : 'See how it did';

  return (
    <div class="sheet-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div class={`sheet ${whole ? 'show-whole' : ''}`} role="dialog" aria-modal="true" aria-label={p.subject} ref={box}>
        <button class="sheet-close" aria-label="Close" onClick={onClose}>
          <Icon name="x" size={18} />
        </button>
        <div class="sheet-info">
          <div class="sheet-photo">{photo && <img src={photo} alt="" />}</div>
          <div class="sheet-words">
            <p class="sheet-when" data-cat={day ? undefined : purposeOf(p)}>
              {day ? (
                <>
                  <Icon name="calendar" size={14} /> {niceDate(day)}, {fromToday(day, today)}
                </>
              ) : (
                <>
                  <i />
                  {catLabel(p)}
                </>
              )}
            </p>
            <h2 class="sheet-subject">{p.subject}</h2>
            <p class="sheet-pre">{p.preheader}</p>
            <p class="sheet-why">{p.why}</p>
            {p.verify?.length ? (
              <div class="sheet-verify">
                <b>
                  <Icon name="alert" size={16} /> Check before sending
                </b>
                <ul>
                  {p.verify.map((v) => (
                    <li key={v}>{v}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
        <div class="sheet-email">{s ? <iframe srcdoc={html} title={`Preview of ${p.subject}`} sandbox="allow-same-origin" /> : <Spinner />}</div>
        <div class="sheet-foot">
          {!used && <span class="sheet-note">Opens as a draft. Nothing sends yet.</span>}
          <Button kind="secondary" class="sheet-toggle" icon={whole ? 'back' : 'eye'} onClick={() => setWhole(!whole)}>
            {whole ? 'Back' : 'Whole email'}
          </Button>
          <Button kind="primary" size="l" icon={used ? 'next' : 'edit'} busy={busy} onClick={go}>
            {label}
          </Button>
        </div>
      </div>
    </div>
  );
}

function WelcomeCard({ used }: { used: Used }) {
  const [busy, setBusy] = useState(false);
  const plan = welcomeEmails();
  const done = plan.some((p) => used.has(p.key));
  const setUp = async () => {
    setBusy(true);
    try {
      await api('/api/sequences/welcome', { method: 'POST' });
      toast('Welcome series is set up. It stays off until you turn it on.');
      navigate('/automatic');
    } catch (e) {
      toastError(e);
      setBusy(false);
    }
  };
  return (
    <section class="lib-welcome">
      <div class="lib-welcome-pics">
        {plan.map((p) => {
          const t = photoOf(p, 's');
          return t ? <img key={p.key} src={t} alt="" loading="lazy" /> : null;
        })}
      </div>
      <div class="grow">
        <h2>Welcome series</h2>
        <p>Three hellos for anyone tagged new-client. They send on their own.</p>
      </div>
      {done ? (
        <Button icon="repeat" onClick={() => navigate('/automatic')}>
          See it
        </Button>
      ) : (
        <Button kind="primary" icon="plus" busy={busy} onClick={setUp}>
          Set it up
        </Button>
      )}
    </section>
  );
}

/** The three welcome emails with a photo each, for the empty Automatic page. */
export function welcomeSteps(): { key: string; when: string; subject: string; photo: string | null }[] {
  return welcomeEmails().map((p) => ({
    key: p.key,
    when: p.sendDay === 0 ? 'Right away' : `${plural(p.sendDay, 'day')} later`,
    subject: p.subject,
    photo: photoOf(p, 's'),
  }));
}

type Pick = { kind: 'draft'; p: PlannedEmail; id: number } | { kind: 'year'; p: PlannedEmail } | { kind: 'month'; p: PlannedEmail };

/** The next dated email. One answer for home and Your year, so they never disagree. */
export interface Dated {
  p: PlannedEmail;
  draftId?: number; // set when this email is already her draft
}

/**
 * The next dated email: her soonest dated draft when it comes before the next unused year email,
 * otherwise that unused email. Anything up to 10 days late still counts.
 */
export function nextDated(used: Used, today: string): Dated | null {
  const plan = yearEmails();
  const next = upNext(plan, new Set(used.keys()), today);
  const cutoff = addDays(today, -10);
  const draftP = plan.find((p) => used.get(p.key)?.status === 'draft' && planDate(p)! >= cutoff);
  if (draftP && (!next || planDate(draftP)! <= planDate(next)!)) return { p: draftP, draftId: used.get(draftP.key)!.id };
  return next ? { p: next } : null;
}

/**
 * What home shows: the next dated email when it is due within a month. Otherwise a library email that fits
 * this month, with the next dated draft (if any) as a second button. Otherwise the next dated email.
 */
function pickNext(emails: EmailSummary[], today: string): { pick: Pick; draft?: { p: PlannedEmail; id: number } } | null {
  const used = usedMap(emails);
  const dated = nextDated(used, today);
  const asPick = (d: Dated): Pick => (d.draftId ? { kind: 'draft', p: d.p, id: d.draftId } : { kind: 'year', p: d.p });
  if (dated && daysBetween(today, planDate(dated.p)!) <= 30) return { pick: asPick(dated) };
  const month = forThisMonth(used, today)[0];
  if (month) return { pick: { kind: 'month', p: month }, draft: dated?.draftId ? { p: dated.p, id: dated.draftId } : undefined };
  return dated ? { pick: asPick(dated) } : null;
}

/** Home screen nudge: the one ready-made email to work on next, with one tap to start it. */
export function UpNext({ emails }: { emails: EmailSummary[] }) {
  const [busy, setBusy] = useState(false);
  const today = centralYmd();
  const found = pickNext(emails, today);
  if (!found) return null;
  const { pick, draft } = found;
  const p = pick.p;
  const day = planDate(p);
  const thumb = photoOf(p, 's');
  const go = async () => {
    setBusy(true);
    try {
      if (pick.kind === 'draft') navigate(`/emails/${pick.id}/edit`);
      else await openPlanned(p);
    } catch (e) {
      toastError(e);
      setBusy(false);
    }
  };
  const when =
    pick.kind === 'draft' ? `Finish your draft · ${niceDate(day!)}, ${fromToday(day!, today)}` : pick.kind === 'year' ? `Up next · ${niceDate(day!)}, ${fromToday(day!, today)}` : 'Good for this month';
  return (
    <section class={`card up-next up-next-${pick.kind}`}>
      {thumb && <img class="up-next-pic" src={thumb} alt="" />}
      <div class="grow">
        <span class="up-next-when">
          <Icon name={pick.kind === 'month' ? 'sparkle' : 'calendar'} size={14} /> {when}
        </span>
        <b class="up-next-name">{cardTitle(p.subject)}</b>
        <div class="up-next-actions">
          <Button kind="primary" icon="edit" busy={busy} onClick={go}>
            {pick.kind === 'draft' ? 'Open your draft' : 'Use this email'}
          </Button>
          {draft && (
            <Button kind="quiet" onClick={() => navigate(`/emails/${draft.id}/edit`)}>
              Open {shortDate(planDate(draft.p)!)} draft
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
